import { FormattedMessage } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { useState } from 'react';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';

//common
import useGeneralHook from 'common/hook/useGeneralHook';
import SchemaForm from 'common/SchemaForm';
import ConfirmationCodeDialog from 'modules/auth/components/ConfirmationCodeDialog';
import { logIn, logInAsGuest, LoginReqData } from '@redux/authReducer';
import { AppState } from '@redux/store';
import { LoginBG, LogoImage, LogoText } from 'svg';
import { PASSWORD } from 'config/regex';
import { TERM_OF_SERVICE_URL } from 'api/API_App';

const LoginPage = () => {
  const [openForgetDialog, setOpenForgetDialog] = useState<boolean>(false);

  const { loading, pendingConfirmationCode } = useSelector((s: AppState) => s.auth, shallowEqual);
  const { dispatch, isMobile, enqueueSnackbar } = useGeneralHook();

  const handleLogin = (values: LoginReqData) => {
    const trimmedValues = {
      handle: values.handle.trim(),
      password: values.password.trim(),
    };

    if (!trimmedValues.handle || !trimmedValues.password) {
      enqueueSnackbar({
        message: 'Vui lòng điền đầy đủ thông tin đăng nhập',
        variant: 'error',
      });

      return;
    }

    dispatch(logIn(trimmedValues));
  };

  const handleOpenForgetDialog = () => {
    setOpenForgetDialog(true);
  };

  const handleCloseForgetDialog = () => {
    setOpenForgetDialog(false);
  };

  return (
    <Box display="flex" height="100vh" width={'100vw'} bgcolor="background.paper">
      <ConfirmationCodeDialog open={pendingConfirmationCode} />
      <Box display="flex" flexDirection="column" height="100%" flex={1}>
        <Box
          display="flex"
          alignItems={'center'}
          height="100%"
          flex={1}
          sx={(theme) => ({
            [theme.breakpoints.down('tablet')]: {
              px: 4,
              pb: 12,
              pt: 4,
              justifyContent: 'flex-start',
            },
          })}
        >
          {!isMobile && (
            <Box flex={1}>
              <img src={LoginBG} alt="login_bg" className="w-full" />
            </Box>
          )}
          <Box className="flex-1 shrink-0 flex flex-col justify-center items-center px-10 py-2 h-full">
            <Box flex={1} />
            <SchemaForm
              onSubmit={handleLogin}
              formHookProps={{
                defaultValues: {
                  handle: '',
                  password: '',
                },
              }}
              formProps={{ sx: { maxWidth: 360 } }}
              schema={{
                fields: ({ formProps: { intl, state, setState } }) => {
                  return {
                    title: {
                      mode: 'node',
                      render: () => (
                        <Box className="flex flex-col items-center justify-center">
                          <Box className="flex gap-2 items-center  mb-10">
                            <LogoImage style={{ height: 50, width: 50 }} />
                            <LogoText />
                          </Box>
                          <Typography variant="h4" color="primary">
                            <FormattedMessage id="login" />
                          </Typography>
                        </Box>
                      ),
                    },
                    handle: {
                      mode: 'text-field',
                      placeholder: intl.formatMessage({ id: 'email' }),
                      rules: { required: true },
                    },
                    password: {
                      mode: 'text-field',
                      type: state.showPassword ? 'text' : 'password',
                      placeholder: intl.formatMessage({ id: 'password' }),
                      InputProps: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                setState({ showPassword: !state.showPassword });
                              }}
                              color="primary"
                            >
                              {state.showPassword ? (
                                <VisibilityOffRounded />
                              ) : (
                                <VisibilityRounded />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                      rules: {
                        required: true,
                        minLength: {
                          value: 8,
                          message: intl.formatMessage({ id: 'account.error.minLength' }),
                        },
                        pattern: {
                          value: PASSWORD,
                          message: intl.formatMessage({ id: 'account.error.invalidPassword' }),
                        },
                      },
                    },
                    forgotPassword: {
                      mode: 'node',
                      render: () => (
                        <>
                          <Box className="flex items-center justify-end">
                            <Button variant="text" onClick={handleOpenForgetDialog}>
                              <FormattedMessage id="forgotPassword" />
                            </Button>
                          </Box>
                          <Dialog
                            open={openForgetDialog}
                            keepMounted
                            onClose={handleCloseForgetDialog}
                            aria-describedby="alert-dialog-slide-description"
                          >
                            <DialogTitle sx={{ textAlign: 'center' }}>
                              <FormattedMessage id="forgotPassword" />
                            </DialogTitle>
                            <Divider variant="middle" sx={{ backgroundColor: 'primary.main' }} />
                            <DialogContent>
                              <DialogContentText id="alert-dialog-slide-description">
                                <FormattedMessage id="pleaseContactAdmin" />
                              </DialogContentText>
                            </DialogContent>
                          </Dialog>
                        </>
                      ),
                    },
                    submit: {
                      mode: 'button',
                      type: 'submit',
                      children: intl.formatMessage({ id: 'login' }),
                      variant: 'outlined',
                      fullWidth: true,
                      loading: loading,
                    },
                    continueAsGuest: {
                      mode: 'node',
                      render: () => (
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          disabled={loading}
                          sx={{ mt: 1.5 }}
                          onClick={() => dispatch(logInAsGuest())}
                        >
                          <span>
                            <FormattedMessage id="continueAsGuest" />
                          </span>
                        </Button>
                      ),
                    },
                  };
                },
              }}
            />
            <Box flex={1} />
            <Box sx={{ maxWidth: '60%' }}>
              <Typography textAlign="center" sx={{ mb: 4 }}>
                <FormattedMessage id="termOfService" />{' '}
                <Link
                  href={TERM_OF_SERVICE_URL}
                  underline="none"
                  sx={{ color: '#3390C4' }}
                  target="_blank"
                >
                  Điều khoản sử dụng
                </Link>
                .
              </Typography>
              <Typography textAlign="center">
                <FormattedMessage id="footer" />
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default LoginPage;
